import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Send,
  X,
  Loader2,
  CheckCircle2,
  Home,
  Wallet,
  TrendingUp,
  CreditCard,
  PiggyBank,
  Brain,
  ArrowRight,
  Sparkles,
  Car,
  Gem,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Conversation stages for progress tracking
const STAGES = [
  { id: "intro", label: "Welcome", icon: Brain },
  { id: "real_estate", label: "Real Estate", icon: Home },
  { id: "vehicles", label: "Vehicles", icon: Car },
  { id: "cash", label: "Cash & Banks", icon: Wallet },
  { id: "investments", label: "Investments", icon: TrendingUp },
  { id: "other_assets", label: "Other Assets", icon: Gem },
  { id: "credits", label: "Credits", icon: CreditCard },
  { id: "income", label: "Income", icon: PiggyBank },
];

export const AIOnboardingAssistant = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentStage, setCurrentStage] = useState(0);
  const [createdItems, setCreatedItems] = useState({ assets: 0, credits: 0, incomes: 0 });
  
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Start conversation when opened
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      startConversation();
    }
  }, [isOpen]);

  const startConversation = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/onboarding/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: "Hello, I'm ready to set up my portfolio.", chat_history: [] }),
      });
      
      if (!response.ok) throw new Error("Failed to start conversation");
      
      const data = await response.json();
      const aiMessage = { role: "assistant", content: data.message };
      setMessages([aiMessage]);
      
      // Process any actions
      if (data.actions?.length > 0) {
        await processActions(data.actions);
      }
      
    } catch (error) {
      console.error("Start conversation error:", error);
      setMessages([{
        role: "assistant",
        content: "Welcome! I'm here to help you set up your portfolio. Your data is secure, not linked to your identity, and editable anytime. Let's start with real estate - do you own any property?"
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async (text) => {
    if (!text.trim() || isLoading) return;
    
    const userMessage = { role: "user", content: text };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/onboarding/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          message: text,
          chat_history: [...messages, userMessage].map(m => ({ role: m.role, content: m.content }))
        }),
      });
      
      if (!response.ok) throw new Error("Chat failed");
      
      const data = await response.json();
      const aiMessage = { role: "assistant", content: data.message };
      setMessages(prev => [...prev, aiMessage]);
      
      // Update stage based on conversation content
      updateStage(data.message);
      
      // Process any actions
      if (data.actions?.length > 0) {
        await processActions(data.actions);
      }
      
    } catch (error) {
      console.error("Send message error:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const processActions = async (actions) => {
    for (const action of actions) {
      try {
        const response = await fetch(`${API_URL}/api/onboarding/execute-action`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(action),
        });
        
        const result = await response.json();
        
        if (result.success) {
          // Update created items count
          if (result.type === "asset") {
            setCreatedItems(prev => ({ ...prev, assets: prev.assets + 1 }));
            toast.success(`Added: ${result.item.name}`);
          } else if (result.type === "credit") {
            setCreatedItems(prev => ({ ...prev, credits: prev.credits + 1 }));
            toast.success(`Added credit: ${result.item.name}`);
          } else if (result.type === "income" || result.type === "expense") {
            setCreatedItems(prev => ({ ...prev, incomes: prev.incomes + 1 }));
            toast.success(`Added: ${result.item.name}`);
          }
        }
      } catch (error) {
        console.error("Execute action error:", error);
      }
    }
  };

  const updateStage = (message) => {
    const lowerMsg = message.toLowerCase();
    if (lowerMsg.includes("vehicle") || lowerMsg.includes("car") || lowerMsg.includes("motorcycle") || lowerMsg.includes("boat")) {
      setCurrentStage(2);
    } else if (lowerMsg.includes("bank") || lowerMsg.includes("cash") || lowerMsg.includes("savings") || lowerMsg.includes("checking")) {
      setCurrentStage(3);
    } else if (lowerMsg.includes("stock") || lowerMsg.includes("crypto") || lowerMsg.includes("etf") || lowerMsg.includes("bitcoin")) {
      setCurrentStage(4);
    } else if (lowerMsg.includes("other valuable") || lowerMsg.includes("jewelry") || lowerMsg.includes("art") || lowerMsg.includes("collectible")) {
      setCurrentStage(5);
    } else if (lowerMsg.includes("other loan") || lowerMsg.includes("student loan") || lowerMsg.includes("credit card debt") || lowerMsg.includes("personal loan")) {
      setCurrentStage(6);
    } else if (lowerMsg.includes("income") || lowerMsg.includes("salary") || lowerMsg.includes("main source")) {
      setCurrentStage(7);
    } else if (lowerMsg.includes("great") && lowerMsg.includes("set up") || lowerMsg.includes("portfolio is set")) {
      setCurrentStage(8);
    }
  };

  const handleComplete = () => {
    onClose();
    navigate("/dashboard");
    toast.success("Portfolio setup complete!");
  };

  if (!isOpen) return null;

  const progress = (currentStage / STAGES.length) * 100;

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col" data-testid="ai-onboarding-assistant">
      {/* Header - Simplified */}
      <div className="border-b border-border bg-card px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Brain className="w-4 h-4 text-primary" />
            </div>
            <h1 className="font-bold">AI Portfolio Setup</h1>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-card/50 px-4 py-2 border-b border-border">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            {STAGES.map((stage, idx) => (
              <div
                key={stage.id}
                className={`flex items-center gap-1 text-xs ${
                  idx <= currentStage ? "text-primary" : "text-muted-foreground"
                }`}
              >
                <stage.icon className="w-3 h-3" />
                <span className="hidden sm:inline">{stage.label}</span>
              </div>
            ))}
          </div>
          <Progress value={progress} className="h-1" />
        </div>
      </div>

      {/* Stats */}
      <div className="bg-secondary/30 px-4 py-2 border-b border-border">
        <div className="max-w-3xl mx-auto flex items-center justify-center gap-6 text-xs">
          <div className="flex items-center gap-1.5">
            <Wallet className="w-3.5 h-3.5 text-primary" />
            <span>{createdItems.assets} assets</span>
          </div>
          <div className="flex items-center gap-1.5">
            <CreditCard className="w-3.5 h-3.5 text-orange-500" />
            <span>{createdItems.credits} credits</span>
          </div>
          <div className="flex items-center gap-1.5">
            <PiggyBank className="w-3.5 h-3.5 text-green-500" />
            <span>{createdItems.incomes} income/expenses</span>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-3xl mx-auto space-y-4">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm ${
                  msg.role === "user"
                    ? "bg-primary text-primary-foreground rounded-br-md"
                    : "bg-card border border-border rounded-bl-md"
                }`}
              >
                {msg.role === "assistant" && (
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="w-3 h-3 text-primary" />
                    <span className="text-[10px] text-primary font-bold uppercase">AI Assistant</span>
                  </div>
                )}
                <div className="whitespace-pre-wrap leading-relaxed">{msg.content}</div>
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-card border border-border px-4 py-3 rounded-2xl rounded-bl-md">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                  <span className="text-sm text-muted-foreground">Thinking...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Complete Button (show when done) */}
      {currentStage >= 7 && (
        <div className="px-4 py-2 bg-green-500/10 border-t border-green-500/20">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 text-green-500">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-sm font-medium">Portfolio setup complete!</span>
            </div>
            <Button onClick={handleComplete} className="bg-green-500 hover:bg-green-600 text-white">
              Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Input Area - Text only */}
      <div className="border-t border-border bg-card px-4 py-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage(input)}
              placeholder="Type your message..."
              disabled={isLoading}
              className="flex-1"
              data-testid="onboarding-input"
            />
            
            <Button
              onClick={() => sendMessage(input)}
              disabled={isLoading || !input.trim()}
              data-testid="onboarding-send"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          
          <p className="text-[10px] text-muted-foreground text-center mt-2">
            Your data is secure and editable anytime
          </p>
        </div>
      </div>
    </div>
  );
};

export default AIOnboardingAssistant;
